import React from 'react';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import * as Sentry from '@sentry/react';

interface State {
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
  scope: string;
}

export class ScopedErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[ScopedErrorBoundary:${this.props.scope}] render error`, error, info);
    Sentry.captureException(error, {
      extra: {
        scope: this.props.scope,
        componentStack: info.componentStack,
      },
    });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="p-4 m-4 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">
              Er ging iets mis bij het laden van dit onderdeel
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
              Probeer het opnieuw of herlaad de pagina.
            </p>
          </div>
          <button
            type="button"
            onClick={this.reset}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white dark:bg-red-900/40 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/60 text-xs font-semibold text-red-700 dark:text-red-300 transition-colors"
          >
            <RefreshCcw size={12} /> Opnieuw
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
